import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation, useParams } from 'react-router-dom';
import { Button, ZoomSVGIcon, LoadMask } from '../../components';
import {
  ZoomCatalog,
  ZoomTableCatalog,
  ZoomTableCatalogHeader,
} from '../../components/media';
import Routes from '../../routes';
import { ErrorSection } from './errorSection';
import { UploadPageParams } from '../uploadPage';
import { wrapRouteWithQuery } from '../../core';
import { ZoomIntegrationTabState } from '../integrations/zoomIntegrationTabState';
import { NotConnectedState } from '../integrations/notConnectedState';
import { DisconnectSection } from '../integrations/disconnectSection';

type Props = {
  model: ZoomIntegrationTabState;
};
export const ZoomTab = observer(({ model }: Props) => {
  const { store } = model;
  const state = model || store.userDashboardPage;
  const { catalog, catalogSource } = state;
  const { isConnected, isLoading, error } = store.zoom;

  useEffect(() => {
    state.pageMounted(isConnected);
    return () => state.pageUnmounted();
  }, [state, isConnected]);

  const location = useLocation();
  const { pathname } = location;

  const params = useParams<UploadPageParams>();
  const { teamId } = params;

  let route;
  if (teamId) {
    route = `${Routes.teamUpload(teamId)}`;
  } else {
    route = Routes.upload();
  }

  if (pathname === route) {
    const returnUrl = wrapRouteWithQuery(route, { view: 'zoom' });
    localStorage.setItem('externalLibraryUploadAuth', returnUrl);
  } else {
    localStorage.removeItem('externalLibraryUploadAuth');
  }

  if (isLoading)
    return (
      <div className="catalog">
        <LoadMask
          className="partial"
          visible={isLoading} />
      </div>
    );

  const disconnectSection = <DisconnectSection
    model={model}
    library={model.libraryService}
    helpBtn={
      <Button
        className="help-btn primary btn-outline medium"
        label="Help"
        route={Routes.zoomHelp()}
        target="_blank"
      />} />;

  if (error) return <ErrorSection header={disconnectSection} />;

  if (!isConnected) return <NotConnected model={state.notConnectedState} />;

  return (
    <>
      <section
        className={`${state.catalog.viewType === 'list' ? 'list-view' : ''
          } page-subheader`}>
        {state.catalog.viewType === 'grid' ? (
          <>
            <div className="flex-left">
              <div className="page-filters">
                <div className="select-controls">
                  {catalogSource.hasMultipleSelection ? (
                    <>
                      <Button
                        label="Cancel"
                        className="primary btn-outline medium"
                        onClick={() => catalogSource.clearMultipleSelection()}
                      />
                      <Button
                        className="primary medium"
                        label="Ingest Selected"
                        onClick={() => state.bulkIngest(teamId)}
                      />
                    </>
                  ) : (
                    <Button
                      className="primary medium"
                      label="Select all"
                      onClick={() => catalogSource.selectAllFiles()}
                      disabled={
                        !catalogSource.entities.length ||
                        !catalogSource.availableforIngestEntities.length
                      }
                    />
                  )}
                </div>
              </div>
            </div>

            {disconnectSection}
          </>
        ) : (
          <ZoomTableCatalogHeader
            teamId={teamId}
            model={catalog}
            source={catalogSource}
            integration={model}
          />
        )}
      </section>

      {isConnected &&
        (state.catalog.viewType === 'grid' ? (
          <ZoomCatalog model={catalog} source={catalogSource} />
        ) : (
          <ZoomTableCatalog model={catalog} source={catalogSource} />
        ))}
    </>
  );
});

type NotConnectedProps = {
  model: NotConnectedState;
};

const NotConnected = observer(({ model }: NotConnectedProps) => {
  const { store } = model;
  const { zoom } = store;

  return (
    <div className="catalog empty-catalog">
      <div className="icon">
        <ZoomSVGIcon />
      </div>
      <h1 className="headline-1">Authorize your Zoom Account</h1>
      <p>To access your Recordings, Cloud Recording must be enabled.</p>

      <Button
        onClick={() => model.triggerConfirmationModal(zoom.authUrl)}
        className="upload-btn secondary large"
        label="Connect with Zoom"
      />

      <Button
        className="link link-primary small"
        route={Routes.zoomHelp()}
        target="_blank"
        label="Need help?"
      />
    </div>
  );
});
